import { ReactElement, forwardRef } from "react";

import { type VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const filterButton = cva(
  "flex items-center gap-2 rounded-full bg-transparent px-3 py-[2px] text-sm text-gray-600 outline-dashed outline-2 outline-gray-200",
  {
    variants: {
      color: {
        brand:
          "outline-brand-300 bg-brand-50 text-brand-500 hover:enabled:bg-brand-100",
        gray: "bg-transparent hover:enabled:bg-gray-100",
      },
      disabled: {
        true: "cursor-default opacity-50",
        false: "",
      },
    },
    defaultVariants: {
      color: "gray",
      disabled: false,
    },
  },
);

export interface FilterButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "color" | "disabled"
    >,
    VariantProps<typeof filterButton> {
  label: string;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  isLoading?: boolean;
  loadingLabel?: string;
  className?: string;
  classNames?: {
    root?: string;
    label?: string;
    loader?: string;
  };
}

const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      label,
      startIcon,
      endIcon,
      color,
      disabled,
      className,
      classNames,
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        className={twMerge(
          filterButton({ color, disabled }),
          className,
          classNames?.root,
        )}
        disabled={disabled || false}
        ref={ref}
        {...rest}
      >
        {startIcon && <span>{startIcon}</span>}
        <span className={classNames?.label}>{label}</span>
        {endIcon && <span>{endIcon}</span>}
      </button>
    );
  },
);

FilterButton.displayName = "FilterButton";

export default FilterButton;
